import React from "react";
import { useGoogleMapsContext } from "@/contexts/GoogleMapsContext";

export interface AddressData {
  addressId: string;
  displayName: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  postalCode: string | null;
  region: string | null;
}

export interface AddressPrediction {
  addressId: string;
  displayName: string;
  types: string[];
}

export interface UseGoogleMapsAutocompleteReturn {
  isLoaded: boolean;
  createAutocompleteSession: () => Promise<any | null>;
  searchAddresses: (
    query: string,
    sessionToken?: any
  ) => Promise<AddressPrediction[]>;
  fetchAddressDetails: (
    addressId: string,
    sessionToken?: any
  ) => Promise<AddressData | null>;
  parsePlaceAddress: (place: any) => AddressData;
}

export const useGoogleMapsAutocomplete =
  (): UseGoogleMapsAutocompleteReturn => {
    const { loadLibraries } = useGoogleMapsContext();

    const placesLibraryRef = React.useRef<any>(null);
    const isLoadingLibraryRef = React.useRef(false);
    const [isPlacesLoaded, setIsPlacesLoaded] = React.useState(false);

    // Eagerly load places library when hook initializes
    React.useEffect(() => {
      const loadPlacesLibrary = async () => {
        if (placesLibraryRef.current || isLoadingLibraryRef.current) {
          return;
        }

        isLoadingLibraryRef.current = true;

        try {
          const { places } = await loadLibraries(["places"]);
          placesLibraryRef.current = places;
          setIsPlacesLoaded(true);
        } catch (error) {
          console.error(
            "Failed to load places library on initialization",
            error
          );
        } finally {
          isLoadingLibraryRef.current = false;
        }
      };

      loadPlacesLibrary();
    }, [loadLibraries]);

    const getPlacesLibrary = React.useCallback(async () => {
      if (placesLibraryRef.current) {
        return placesLibraryRef.current;
      }

      // Wait a bit if library is currently loading
      if (isLoadingLibraryRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        return placesLibraryRef.current;
      }

      console.error("Places library not loaded");
      return null;
    }, []);

    const parsePlaceAddress = React.useCallback((place: any): AddressData => {
      const addressData: AddressData = {
        addressId: place.id,
        displayName: null,
        address1: null,
        address2: null,
        city: null,
        country: null,
        latitude: place.location?.lat() || null,
        longitude: place.location?.lng() || null,
        postalCode: null,
        region: null,
      };

      let streetNumber = "";
      let streetName = "";

      if (Array.isArray(place.addressComponents)) {
        place.addressComponents.forEach((component: any) => {
          const types = component.types || [];
          const longName = component.longText || "";
          const shortName = component.shortText || "";

          if (types.includes("street_number")) {
            streetNumber = longName;
          }

          if (types.includes("route")) {
            streetName = longName;
          }

          if (types.includes("subpremise")) {
            addressData.address2 = longName;
          }

          if (types.includes("locality")) {
            addressData.city = longName;
          }

          if (types.includes("administrative_area_level_1")) {
            addressData.region = shortName;
          }

          if (types.includes("postal_code")) {
            addressData.postalCode = longName;
          }

          if (types.includes("country")) {
            addressData.country = shortName;
          }
        });
      }

      addressData.displayName =
        place.displayName?.toString() || place.formattedAddress || "";
      addressData.address1 = [streetNumber, streetName]
        .filter((part: string) => part.trim() !== "")
        .join(" ");

      return addressData;
    }, []);

    const createAutocompleteSession = React.useCallback(async () => {
      const placesLibrary = await getPlacesLibrary();

      if (!placesLibrary) {
        console.error("Places library not available for session token");
        return null;
      }

      const { AutocompleteSessionToken } = placesLibrary;

      if (!AutocompleteSessionToken) {
        console.error("AutocompleteSessionToken not available");
        return null;
      }

      return new AutocompleteSessionToken();
    }, [getPlacesLibrary]);

    const searchAddresses = React.useCallback(
      async (
        query: string,
        sessionToken: any = null
      ): Promise<AddressPrediction[]> => {
        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary || !placesLibrary.Place) {
          console.error("Places library not available for predictions");
          return [];
        }

        const { AutocompleteSuggestion } = placesLibrary;

        if (!AutocompleteSuggestion) {
          console.error(
            "AutocompleteSuggestion not available in places library"
          );
          return [];
        }

        try {
          const request: any = {
            input: query,
            includedPrimaryTypes: ["street_address", "premise"],
          };

          if (sessionToken) {
            request.sessionToken = sessionToken;
          }

          const { suggestions } =
            await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

          if (!suggestions || suggestions.length === 0) {
            return [];
          }

          const predictions: AddressPrediction[] = suggestions.map(
            (suggestion: any) => {
              const prediction = suggestion.placePrediction;

              return {
                addressId: prediction.placeId,
                displayName: prediction.text?.toString() || "",
                types: prediction.types || [],
              };
            }
          );

          return predictions;
        } catch (error) {
          console.error("Failed to fetch autocomplete suggestions:", error);
          return [];
        }
      },
      [getPlacesLibrary]
    );

    const fetchAddressDetails = React.useCallback(
      async (
        addressId: string,
        sessionToken: any = null
      ): Promise<AddressData | null> => {
        if (!addressId) {
          console.error("No addressId provided to fetchAddressDetails");
          return null;
        }

        const placesLibrary = await getPlacesLibrary();

        if (!placesLibrary) {
          console.error("Places library not available for place details");
          return null;
        }

        try {
          const { Place } = placesLibrary;

          if (!Place) {
            console.error("Place class not available in places library");
            return null;
          }

          const place = new Place({ id: addressId });

          const fetchOptions: any = {
            fields: [
              "addressComponents",
              "formattedAddress",
              "location",
              "displayName",
            ],
          };

          if (sessionToken) {
            fetchOptions.sessionToken = sessionToken;
          }

          await place.fetchFields(fetchOptions);

          const addressData = parsePlaceAddress(place);

          return addressData;
        } catch (error) {
          console.error("Failed to fetch address details:", error);
          return null;
        }
      },
      [getPlacesLibrary, parsePlaceAddress]
    );

    return {
      isLoaded: isPlacesLoaded,
      createAutocompleteSession,
      searchAddresses,
      fetchAddressDetails,
      parsePlaceAddress,
    };
  };
